import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { HeritageSeal, DahliaMark } from '@/components/BrandMark';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

const POLL_INTERVAL = 2000;
const MAX_POLLS = 15; // 30 seconds

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') || '';
  const [state, setState] = useState({ status: 'polling', tx: null, error: null });
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setState({ status: 'error', tx: null, error: 'Missing session id.' }); return; }
    let attempts = 0;
    let cancelled = false;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${encodeURIComponent(sessionId)}`);
        if (cancelled) return;
        if (data.payment_status === 'paid') {
          setState({ status: 'paid', tx: data, error: null });
          return;
        }
        if (data.status === 'expired' || data.payment_status === 'unpaid' && attempts >= MAX_POLLS) {
          setState({ status: 'unpaid', tx: data, error: 'Payment not received.' });
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState({ status: 'unknown', tx: data, error: 'Still processing. Check your email — we&rsquo;ll confirm as soon as Stripe reports back.' });
          return;
        }
        setTimeout(tick, POLL_INTERVAL);
      } catch (e) {
        if (cancelled) return;
        setState({ status: 'error', tx: null, error: e?.response?.data?.detail || 'Could not read payment status.' });
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  const goHome = () => {
    const kind = state.tx?.kind;
    if (kind === 'founder_order') navigate('/console/founder/orders');
    else if (kind === 'founder_tier') navigate('/console/founder');
    else if (kind === 'shop_order') navigate('/store');
    else navigate('/');
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-10" data-testid="payment-success">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <HeritageSeal size={72} />
          <div className="h-eyebrow mt-3">Heritage Apparel</div>
        </div>
        <Panel className="p-8 text-center">
          {state.status === 'polling' && (
            <>
              <Loader2 size={40} className="animate-spin mx-auto" style={{ color: 'var(--heritage-plum)' }} />
              <div className="h-serif text-2xl mt-4" style={{ color: 'var(--heritage-plum)' }}>Confirming your payment…</div>
              <div className="text-sm opacity-80 mt-1" style={{ color: 'var(--heritage-plum)' }}>Please keep this tab open. Stripe usually confirms in a few seconds.</div>
            </>
          )}
          {state.status === 'paid' && (
            <>
              <CheckCircle2 size={48} className="mx-auto" style={{ color: '#5B6E2C' }} />
              <div className="h-display text-3xl mt-3" style={{ color: 'var(--heritage-plum)' }}>Payment received</div>
              <div className="text-sm opacity-85 mt-2" style={{ color: 'var(--heritage-plum)' }}>
                Thank you. Your Heritage {state.tx?.kind === 'founder_tier' ? 'Founders Club seat' : 'order'} is confirmed and a receipt has been emailed to you.
              </div>
              {state.tx?.metadata?.invoice_number && (
                <div className="mt-3 h-eyebrow" style={{ color: 'var(--heritage-plum)' }} data-testid="payment-success-invoice">
                  Order {state.tx.metadata.invoice_number}
                </div>
              )}
              {state.tx?.amount && (
                <div className="mt-4 rounded-lg py-3 px-4 inline-block" style={{ background: 'rgba(75,24,56,0.05)', border: '1px solid rgba(75,24,56,0.15)' }}>
                  <div className="h-serif text-2xl" style={{ color: 'var(--heritage-plum)' }}>${Number(state.tx.amount).toFixed(2)} {String(state.tx.currency || 'NZD').toUpperCase()}</div>
                </div>
              )}
              <button onClick={goHome} className="h-btn mt-6" data-testid="payment-success-continue">Continue</button>
            </>
          )}
          {state.status === 'unpaid' && (
            <>
              <XCircle size={44} className="mx-auto" style={{ color: '#9A2C2C' }} />
              <div className="h-serif text-2xl mt-3" style={{ color: 'var(--heritage-plum)' }}>Payment not received</div>
              <div className="text-sm opacity-85 mt-2" style={{ color: 'var(--heritage-plum)' }}>{state.error}</div>
              <Link to="/" className="h-btn-outline mt-5 inline-flex text-xs">Back to Heritage</Link>
            </>
          )}
          {state.status === 'unknown' && (
            <>
              <Loader2 size={40} className="mx-auto opacity-60" style={{ color: 'var(--heritage-plum)' }} />
              <div className="h-serif text-2xl mt-3" style={{ color: 'var(--heritage-plum)' }}>Still processing</div>
              <div className="text-sm opacity-85 mt-2" style={{ color: 'var(--heritage-plum)' }}>{state.error}</div>
              <button onClick={goHome} className="h-btn-outline mt-5 text-xs">Continue</button>
            </>
          )}
          {state.status === 'error' && (
            <>
              <XCircle size={44} className="mx-auto" style={{ color: '#9A2C2C' }} />
              <div className="h-serif text-2xl mt-3" style={{ color: 'var(--heritage-plum)' }}>Something went wrong</div>
              <div className="text-sm opacity-85 mt-2" style={{ color: 'var(--heritage-plum)' }}>{state.error}</div>
              <Link to="/" className="h-btn-outline mt-5 inline-flex text-xs">Back to Heritage</Link>
            </>
          )}
          <div className="pt-5 mt-5 border-t flex items-center justify-center gap-2" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
            <DahliaMark size={14} />
            <span className="text-[10px] h-eyebrow" style={{ color: 'var(--heritage-plum)' }}>Heritage Apparel</span>
          </div>
        </Panel>
      </div>
    </div>
  );
}
